import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, subMonths, isSameMonth, isSameDay, isWeekend,
  isBefore, startOfDay,
} from 'date-fns';
import { parseDate } from '../../utils/dates';
import './DatePicker.css';

function DatePicker({ selectedDays, onChange, holidays = [], disabledDates = [] }) {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const today = startOfDay(new Date());

  // Build calendar grid
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calStart = startOfWeek(monthStart);
    const calEnd = endOfWeek(monthEnd);

    const days = [];
    let day = calStart;
    while (day <= calEnd) {
      days.push(new Date(day));
      day = addDays(day, 1);
    }
    return days;
  }, [currentMonth]);

  // Convert selected days to a map for quick lookup
  const selectedMap = useMemo(() => {
    const map = {};
    for (const d of selectedDays) {
      map[d.date] = d;
    }
    return map;
  }, [selectedDays]);

  const holidaySet = useMemo(() => new Set(holidays), [holidays]);
  const disabledSet = useMemo(() => new Set(disabledDates), [disabledDates]);

  const handleDayClick = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');

    if (selectedMap[dateStr]) {
      // Remove
      onChange(selectedDays.filter(d => d.date !== dateStr));
    } else {
      // Add as full day by default
      onChange([...selectedDays, { date: dateStr, day_type: 'full' }]);
    }
  };

  const handleDayTypeChange = (dateStr, dayType) => {
    onChange(selectedDays.map(d =>
      d.date === dateStr ? { ...d, day_type: dayType } : d
    ));
  };

  const removeDay = (dateStr) => {
    onChange(selectedDays.filter(d => d.date !== dateStr));
  };

  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));
  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  // Sort selected days for display
  const sortedSelected = [...selectedDays].sort((a, b) => a.date.localeCompare(b.date));

  return (
    <div className="date-picker">
      <div className="date-picker-calendar">
        <div className="date-picker-nav">
          <button type="button" onClick={prevMonth} className="date-picker-nav-btn">
            <ChevronLeft size={16} />
          </button>
          <span className="date-picker-month">{format(currentMonth, 'MMMM yyyy')}</span>
          <button type="button" onClick={nextMonth} className="date-picker-nav-btn">
            <ChevronRight size={16} />
          </button>
        </div>

        <div className="date-picker-grid">
          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => (
            <div key={d} className="date-picker-weekday">{d}</div>
          ))}

          {calendarDays.map((day, i) => {
            const dateStr = format(day, 'yyyy-MM-dd');
            const isSelected = !!selectedMap[dateStr];
            const isCurrentMonth = isSameMonth(day, currentMonth);
            const isToday = isSameDay(day, today);
            const isPast = isBefore(day, today);
            const isHoliday = holidaySet.has(dateStr);
            const isDisabled = disabledSet.has(dateStr);
            const isWknd = isWeekend(day);

            const classes = [
              'date-picker-day',
              !isCurrentMonth && 'date-picker-day-other',
              isSelected && 'date-picker-day-selected',
              isToday && 'date-picker-day-today',
              isPast && 'date-picker-day-past',
              isHoliday && 'date-picker-day-holiday',
              isWknd && 'date-picker-day-weekend',
              isDisabled && 'date-picker-day-disabled',
            ].filter(Boolean).join(' ');

            return (
              <button
                key={i}
                type="button"
                className={classes}
                onClick={() => !isPast && isCurrentMonth && handleDayClick(day)}
                disabled={isPast || !isCurrentMonth}
                title={isHoliday ? 'Holiday' : isDisabled ? 'Overlap' : ''}
              >
                {format(day, 'd')}
              </button>
            );
          })}
        </div>
      </div>

      {/* Selected days list with day type toggle */}
      {sortedSelected.length > 0 && (
        <div className="date-picker-selected">
          <div className="date-picker-selected-header">
            Selected ({sortedSelected.length} day{sortedSelected.length !== 1 ? 's' : ''})
          </div>
          <div className="date-picker-selected-list">
            {sortedSelected.map(day => (
              <div key={day.date} className="date-picker-selected-item">
                <span className="date-picker-selected-date">
                  {format(parseDate(day.date), 'EEE, MMM d')}
                </span>
                <select
                  value={day.day_type}
                  onChange={(e) => handleDayTypeChange(day.date, e.target.value)}
                  className="date-picker-day-type-select"
                >
                  <option value="full">Full day</option>
                  <option value="half_am">Half (AM)</option>
                  <option value="half_pm">Half (PM)</option>
                </select>
                <button
                  type="button"
                  className="date-picker-remove-btn"
                  onClick={() => removeDay(day.date)}
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default DatePicker;
